import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Trash2, LoaderCircle, ShieldCheck, TriangleAlert, UtensilsCrossed, Wheat, Truck, Check, CircleCheckBig, RefreshCw, Info } from 'lucide-react';
import api from '../../api/axios';
import SearchSelect from '../../components/SearchSelect';

// İdxal v2 → «Təmizlə» (FiscalSTOK-dan köçürülüb): seçilmiş tenantın bölmələrini boşalt (yenidən import üçün).
// Qoruma serverdədir: ad təsdiqi, silmədən əvvəl avtomatik tam nüsxə, hər DELETE sayla yoxlanılır
// (başqa tenantın sətri silinə bilməz). Nüsxə Baza Restore → «Təhlükəsizlik nüsxələri»-ndən qaytarılır.
const ICON = { menu: UtensilsCrossed, ingredients: Wheat, suppliers: Truck };
const CARD = 'rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

export default function ClearTenantData({ tenants = [] }) {
  const [tenantId, setTenantId] = useState('');
  const [counts,   setCounts]   = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [sel,      setSel]      = useState([]);
  const [confirm,  setConfirm]  = useState('');
  const [busy,     setBusy]     = useState(false);
  const [result,   setResult]   = useState(null);

  const tenant = tenants.find(t => String(t.id) === String(tenantId));
  const tname = tenant ? String(tenant.name || '').trim() : '';
  const nameOk = !!tname && confirm.trim() === tname;

  async function loadCounts(id = tenantId) {
    if (!id) { setCounts(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/tenants/${id}/data-counts`);
      if (!data.ok) { toast.error(data.msg || 'Saylar alınmadı'); setCounts(null); return; }
      setCounts(data.data);
    } catch { toast.error('Saylar alınmadı'); setCounts(null); }
    finally { setLoading(false); }
  }
  useEffect(() => { setSel([]); setConfirm(''); setResult(null); loadCounts(tenantId); }, [tenantId]); // eslint-disable-line

  const toggle = k => setSel(s => (s.includes(k) ? s.filter(x => x !== k) : [...s, k]));
  const total = counts ? sel.reduce((n, k) => n + (counts[k]?.count || 0), 0) : 0;

  async function doClear() {
    if (!tenant || !sel.length || !nameOk) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/superadmin/tenants/${tenant.id}/clear-data`, { sections: sel, confirmName: confirm.trim() }, { timeout: 0 });
      if (!data.ok) { toast.error(data.msg || 'Silinmədi', { duration: 8000 }); return; }
      setResult(data);
      setSel([]); setConfirm('');
      toast.success('Təmizləndi · əvvəlki vəziyyətin nüsxəsi saxlandı', { duration: 6000 });
      loadCounts(tenant.id);
    } catch (e) {
      toast.error(e?.response?.data?.msg || 'Silinmədi, heç nə dəyişmədi', { duration: 8000 });
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <section className={`${CARD} p-5 sm:p-7`}>
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">Təmizlə</p>
        <h3 className="mt-1 text-lg font-semibold tracking-tight text-slate-900">Tenantın məlumatını boşalt</h3>
        <p className="mt-0.5 text-sm text-slate-500">Test importundan sonra seçilmiş bölmələri boşaldıb yenidən import etmək üçün. Yalnız seçdiyin tenant və seçdiyin bölmələr silinir.</p>

        <div className="mt-5 grid gap-6 lg:grid-cols-[300px_1fr]">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slate-600">Tenant (restoran)</label>
            <SearchSelect value={tenantId} onChange={setTenantId} placeholder="Tenant seçin"
              options={tenants.map(t => ({ value: String(t.id), label: t.name || t.username || `Tenant ${t.id}` }))} />
            <div className="mt-4 space-y-2 rounded-xl bg-emerald-50/60 p-3.5 text-xs leading-relaxed text-emerald-900 ring-1 ring-emerald-200/60">
              <p className="flex items-center gap-1.5 font-semibold"><ShieldCheck className="h-4 w-4" /> Qoruma</p>
              <p>Silmədən əvvəl tenantın hazırkı vəziyyəti serverdə avtomatik nüsxələnir. Səhv olsa <b>Baza Restore → Təhlükəsizlik nüsxələri</b>-ndən geri qaytarılır.</p>
              <p>Hər silmə əvvəlcə bu tenant üçün sayılır; silinən say uyğun gəlməsə heç nə silinmir. Başqa tenantın datasına toxunulmur.</p>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <label className="text-xs font-medium text-slate-600">Bölmələr</label>
              {tenant && (
                <button type="button" onClick={() => loadCounts()} disabled={loading}
                  className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 hover:bg-slate-100">
                  <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenilə
                </button>
              )}
            </div>
            {!tenant ? (
              <div className="flex h-40 items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 text-sm text-slate-400">Əvvəlcə tenant seçin</div>
            ) : loading && !counts ? (
              <div className="flex h-40 items-center justify-center gap-2 text-sm text-slate-400"><LoaderCircle className="h-4 w-4 animate-spin" /> Saylar yüklənir</div>
            ) : counts && (
              <div className="grid gap-2.5 sm:grid-cols-2">
                {Object.entries(counts).map(([k, v]) => {
                  const on = sel.includes(k);
                  const Ic = ICON[k] || UtensilsCrossed;
                  const empty = !v.count;
                  return (
                    <button key={k} type="button" disabled={empty} onClick={() => toggle(k)}
                      className={`flex items-center gap-3 rounded-xl p-3.5 text-left transition-all disabled:cursor-not-allowed disabled:opacity-50 ${on ? 'border-2 border-red-400 bg-red-50/60' : 'border border-slate-200 bg-white hover:border-slate-300'}`}>
                      <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${on ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-500'}`}><Ic className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm font-semibold ${on ? 'text-red-800' : 'text-slate-800'}`}>{v.label}</span>
                        <span className="block text-xs text-slate-500">{empty ? 'Boşdur' : `${v.count} sətir`}{v.hint ? ` · ${v.hint}` : ''}</span>
                      </span>
                      <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${on ? 'border-red-500 bg-red-500 text-white' : 'border-slate-300 bg-white'}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {counts && (
              <p className="mt-3 flex gap-2 rounded-xl bg-slate-50 p-3 text-xs leading-relaxed text-slate-500">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                Sifarişlər, ödənişlər, kassa və hesabat tarixçəsi silinmir. Menyu silinsə köhnə sifarişlər yenə adı ilə görünür.
              </p>
            )}
          </div>
        </div>

        {tenant && sel.length > 0 && (
          <div className="mt-6 rounded-2xl bg-red-50/70 p-4 ring-1 ring-red-200/70 sm:p-5">
            <p className="flex items-center gap-2 text-sm font-semibold text-red-800"><TriangleAlert className="h-4 w-4" /> {tname} · {total} sətir silinəcək</p>
            <p className="mt-1 text-xs text-red-700/90">Təsdiq üçün restoranın adını olduğu kimi yazın: <b className="font-mono">{tname}</b></p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Restoran adı"
                className={`min-w-0 flex-1 rounded-xl border bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-4 ${nameOk ? 'border-red-300 focus:ring-red-100' : 'border-slate-200 focus:border-sky-400 focus:ring-sky-100'}`} />
              <button type="button" onClick={doClear} disabled={!nameOk || busy}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40">
                {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {busy ? 'Silinir' : 'Seçilənləri sil'}
              </button>
            </div>
          </div>
        )}

        {result && (
          <div className="mt-6 flex gap-3 rounded-2xl bg-emerald-50/70 p-4 ring-1 ring-emerald-200/60">
            <CircleCheckBig className="h-5 w-5 flex-shrink-0 text-emerald-600" />
            <div className="min-w-0 text-sm">
              <p className="font-semibold text-emerald-900">Təmizləndi</p>
              <p className="text-xs text-emerald-800/90">
                {Object.entries(result.deleted || {}).map(([k, n]) => `${counts?.[k]?.label?.split(' (')[0] || k}: ${n}`).join(' · ')}
              </p>
              <p className="mt-1 truncate text-xs text-emerald-800/80">Əvvəlki vəziyyətin nüsxəsi: <span className="font-mono">{result.safety}</span></p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
